<template>
  <g v-if="width > 0" class="z-10 origin-center" :class="`${color}-container`">
    <path
      v-for="(point, idx) of renderablePoints"
      :key="idx"
      v-tooltip="point.label"
      class="ease-fast-effect stroke-[8px] duration-400"
      :class="[
        !isActive && 'opacity-0 transition-none',
        isActive && 'opacity-100 transition-all',
        point.delayClass,
        point.colorClasses,
        point.type === 'self' && !props.splitByPerspective && `stroke-${color}-90`,
      ]"
      :transform="getTranslate(point)"
      :d="point.shape"
    />
  </g>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { shapePaths } from '../../../base/shapes/shape-paths'
  import {
    colorClassesForPerspective,
    shapeForPerspective,
  } from '@/core/models/feedback-perspective'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'

  interface Props {
    color: string
    totalNumberOfSegments: number
    index: number
    width: number
    hovered: boolean
    maxValue: number
    topicPerspectives: ReadonlyArray<Perspective>
    splitByPerspective: boolean
    perspectiveAverages: ReadonlyArray<{ key: string; average: number | null }>
  }

  const props = defineProps<Props>()

  const skillAngle = computed(() => {
    return (2 * Math.PI) / props.totalNumberOfSegments
  })

  const getTranslate = (point: RenderablePoint) => {
    if (!isActive.value) {
      return `translate(${props.width * 0.5}, ${props.width * 0.5}) scale(0)`
    }

    if (point.perspectiveIndex === 2) {
      return `translate(${point.x}, ${point.y}) scale(0.17) rotate(${point.rotateAngle * (-180 / Math.PI) - 45})`
    }

    return `translate(${point.x}, ${point.y}) scale(0.18) rotate(${point.rotateAngle * (-180 / Math.PI)})`
  }

  const isActive = computed(() => {
    return props.hovered
  })

  interface RenderablePoint {
    x: number
    y: number
    delayClass: string
    label: string
    type: string
    shape: string
    rotateAngle: number
    colorClasses: string
    perspectiveIndex: number
  }

  const { t } = useI18n()

  const BASE_RADIUS_PERCENT = 50

  const renderablePoints = computed<RenderablePoint[]>(() => {
    const pointsWithValues = props.perspectiveAverages.filter(
      (entry): entry is { key: string; average: number } => typeof entry.average === 'number',
    )

    const numberOfPoints = pointsWithValues.length
    if (numberOfPoints === 0) {
      return []
    }

    return pointsWithValues.map((entry, dotIndex) =>
      createPointConfig(entry.average, angleForDot(dotIndex, numberOfPoints), entry.key),
    )
  })

  const angleForDot = (dotIndex: number, numberOfPoints: number) => {
    const slotAngle = skillAngle.value / (numberOfPoints + 1)

    if (numberOfPoints % 2 === 1) {
      if (dotIndex === 0) {
        return 0
      }
      const stepsFromCenter = Math.ceil(dotIndex / 2)
      const direction = dotIndex % 2 === 1 ? 1 : -1
      return direction * stepsFromCenter * slotAngle
    }

    const offsetFromCenter = dotIndex - (numberOfPoints - 1) / 2
    return offsetFromCenter * slotAngle
  }

  const createPointConfig = (
    value: number,
    jitterRadians: number,
    key: string,
  ): RenderablePoint => {
    const isSelf = key === SELF_PERSPECTIVE_KEY

    const normalizedScale =
      (value * (props.maxValue - 1)) / (props.maxValue + 1) + 2 * (1 / (props.maxValue + 1))

    const xOffset = Math.sin(jitterRadians) * normalizedScale * BASE_RADIUS_PERCENT
    const yOffset = Math.cos(jitterRadians) * normalizedScale * BASE_RADIUS_PERCENT

    const x = ((BASE_RADIUS_PERCENT - xOffset) / 100) * props.width
    const y = ((BASE_RADIUS_PERCENT - yOffset) / 100) * props.width

    const shape = shapeForPerspective(key)
    const label = resolvePerspectiveLabel(key, props.topicPerspectives, {
      selfLabel: t('perspectives.self'),
    })
    const perspectiveIndex = props.topicPerspectives.findIndex((p) => p.key === key)
    return {
      x,
      y,
      label,
      type: isSelf ? 'self' : key,
      delayClass: isSelf ? '' : getDelayClass(value),
      shape: shapePaths[shape],
      colorClasses: colorClassesForPerspective(key, props.topicPerspectives, 'svg'),
      rotateAngle: jitterRadians,
      perspectiveIndex,
    }
  }

  const getDelayClass = (value: number) => {
    return [
      'delay-[0ms]',
      'delay-[40ms]',
      'delay-[80ms]',
      'delay-[120ms]',
      'delay-[160ms]',
      'delay-[200ms]',
    ][Math.floor(value * 6)]
  }
</script>
